import { Col, Row, Spinner } from "react-bootstrap";
import { Scheduler, DayView } from "@progress/kendo-react-scheduler";
import { SearchableLocationDropdown, SpinnerDefault, TitlelessCard } from "../../components";
import { MultiSelectProviderDropdown } from "../../components/MultiSelectDropdown";
import { useAppointmentClipboard, useCalendarEvents, useCalendarLocationSelection, useCalendarProviderSchedules, useStickySchedulerChrome } from "./hooks";
import { useSetPageTitle } from "../../hooks";
import { useEffect, useMemo, useRef, useCallback } from "react";
import { AppointmentContextMenu, BlockedTimeEditSlot, BlockedTimeSlot, CalendarBookingModal, HeldAppointmentChip, NameTitleTimeItem, SchedulerContextHeader } from "./components";
import { AttendanceStatus } from "../../data/entities";
import { ParkedAppointment } from "./hooks/useAppointmentClipboard";
import "@progress/kendo-date-math/tz/all";


const HiddenDateHeaderCell = () => null;

export function CalendarLocationPage() {
  useSetPageTitle("Locations Calendar", []);

  const { 
    date, 
    mapResult, 
    group, 
    isLoading, 
    resources, 
    setCalendarGrouping, 
    handleInputChange, 
    handleManualDataChange,
    refreshCalendarEvents,
    checkDateRangeAndRefresh,
    updateAttendanceStatus,
    findCalendarEvent,
    moveCalendarEvent
  } = useCalendarEvents();

  const {
    selectedLocationId,
    selectedProviders,
    locationOptions,
    providerOptions,
    isInitialized,
    dropdownResults,
    filteredDetails,
    showLocationDefaultItem,
    handleSelectLocation,
    handleSelectProviders,
    getProviderIdsString,
  } = useCalendarLocationSelection();

  const providerIds = useMemo(() => 
    filteredDetails.map(detail => detail.providerId),
    [filteredDetails]
  );

  const { schedules, isLoading: schedulesLoading } = useCalendarProviderSchedules(providerIds);

  const providerIdsString = useMemo(() => 
    getProviderIdsString(),
    [getProviderIdsString]
  );

  const schedulerContainerRef = useStickySchedulerChrome(isInitialized);

  const prevDateRef = useRef(date);
  const prevLocationIdRef = useRef(selectedLocationId);
  const prevProviderIdsStringRef = useRef<string | null>(providerIdsString);

  const dataChangeRef = useRef(handleManualDataChange);
  dataChangeRef.current = handleManualDataChange;

  const handlePasteAppointment = useCallback((item: any) => {
    dataChangeRef.current({ created: [item], updated: [], deleted: [] } as any);
  }, []);

  const handleMoveAppointment = useCallback((
    parkedItem: ParkedAppointment,
    target: { start: Date; end: Date; providerId?: number }
  ) => {
    if (!parkedItem.sourceEvent) return;
    moveCalendarEvent(parkedItem.sourceEvent, target);
  }, [moveCalendarEvent]);

  const {
    parked,
    menu,
    menuRef,
    openItemMenu,
    openSlotMenu,
    copyFromMenu,
    cutFromMenu,
    placeFromMenu,
    clearParked,
    closeMenu,
  } = useAppointmentClipboard({
    onPasteAppointment: handlePasteAppointment,
    onMoveAppointment: handleMoveAppointment,
    findCalendarEvent,
  });

  const visibleEvents = useMemo(() =>
    parked?.mode === "cut" && parked.bookingEventId != null
      ? mapResult.filter(e => e.bookingEventId !== parked.bookingEventId)
      : mapResult,
  [mapResult, parked]);

  const handleMenuSetAttendance = useCallback((dataItem: any, status: AttendanceStatus) => {
    updateAttendanceStatus(dataItem.bookingEventId, status);
    closeMenu();
  }, [updateAttendanceStatus, closeMenu]);

  // Update calendar when location or providers change
  useEffect(() => {
    if (!isInitialized || !selectedLocationId) return;

    const locationOrProvidersChanged = 
      prevLocationIdRef.current !== selectedLocationId || 
      prevProviderIdsStringRef.current !== providerIdsString;

    prevLocationIdRef.current = selectedLocationId;
    prevProviderIdsStringRef.current = providerIdsString;

    // If no providers selected, clear calendar
    if (providerIdsString === null) {
      handleInputChange([], "calendarEvents");
      setCalendarGrouping("provider", [], "providerId", "providerName", true);
      return;
    }

    // Update calendar grouping
    setCalendarGrouping("provider", filteredDetails, "providerId", "providerName");

    if (locationOrProvidersChanged) {
      refreshCalendarEvents("getByLocationProviderAsync", selectedLocationId, providerIdsString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, providerIdsString, isInitialized]);

  // Check if date navigation requires fetching new data (when navigating outside cached range)
  useEffect(() => {
    if (!isInitialized || !selectedLocationId) return;
    
    if (providerIdsString === null) return;
    
    if (prevDateRef.current.getTime() !== date.getTime()) {
      prevDateRef.current = date;
      checkDateRangeAndRefresh("getByLocationProviderAsync", selectedLocationId, providerIdsString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isInitialized, selectedLocationId, providerIdsString]);

  const memoizedFormComponent = useCallback((formProps: any) => (
    <CalendarBookingModal
      {...formProps}
      dropdownResults={dropdownResults}
      onDeleteAppointment={(dataItem: any) =>
        dataChangeRef.current({ created: [], updated: [], deleted: [dataItem] } as any)
      }
    />
  ), [dropdownResults]);

  const memoizedSlot = useCallback((props: any) => (
    <BlockedTimeSlot
      {...props}
      schedules={schedules}
      providers={filteredDetails}
      onSlotContextMenu={openSlotMenu}
    />
  ), [schedules, filteredDetails, openSlotMenu]);

  const memoizedItem = useCallback((itemProps: any) => (
    <NameTitleTimeItem
      {...itemProps}
      onItemContextMenu={openItemMenu}
    />
  ), [openItemMenu]);

  const memoizedEditSlot = useCallback((props: any) => (
    <BlockedTimeEditSlot
      {...props}
      schedules={schedules}
      providers={filteredDetails}
    />
  ), [schedules, filteredDetails]);

  const schedulerLocationName = useMemo(() =>
    locationOptions.find(o => o.value === selectedLocationId)?.text,
  [locationOptions, selectedLocationId]);

  const memoizedHeader = useCallback((headerProps: any) => (
    <SchedulerContextHeader
      {...headerProps}
      name={schedulerLocationName}
      extraChip={parked && <HeldAppointmentChip parked={parked} onClear={clearParked} />}
    />
  ), [schedulerLocationName, parked, clearParked]);

  if (!isInitialized) {
    return <SpinnerDefault />;
  }

  return (
    <TitlelessCard bodyClassName="p-0">
      <Row>
        <Col xs={24} sm={12} md={12}>
          <div>
            <SearchableLocationDropdown
              name="Location"
              defaultItem={showLocationDefaultItem}
              locations={locationOptions}
              value={selectedLocationId}
              onChange={handleSelectLocation}
              enableRef={true}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={12}>
          <MultiSelectProviderDropdown
            name="providers"
            providers={providerOptions}
            filterable={true}
            onChange={handleSelectProviders}
            enableRef={true}
            value={selectedProviders}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="scheduler-container scheduler-sticky-header scheduler-single-day" ref={schedulerContainerRef}>
            {(isLoading || schedulesLoading) && (
              <div className="scheduler-overlay">
                <Spinner 
                  size="sm" 
                  animation="border" 
                  variant="primary" 
                  style={{ width: "5rem", height: "5rem", borderWidth: "0.5rem" }}
                />
              </div>
            )}
            <Scheduler
              data={visibleEvents}
              date={date}
              timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
              onDateChange={({ value }) => handleInputChange(value, "dateSelection")}
              onDataChange={handleManualDataChange}
              height="100%"
              group={group}
              resources={resources}
              item={memoizedItem}
              editable
              header={memoizedHeader}
              form={memoizedFormComponent}
              //editSlot={memoizedEditSlot}
            >
              <DayView
                title="Day"
                numberOfDays={1}
                slotDuration={60}
                slotDivisions={4}
                startTime="07:00"
                endTime="22:00"
                workDayStart="07:00"
                workDayEnd="22:00"
                slot={memoizedSlot}
                dateHeaderCell={HiddenDateHeaderCell}
              />
            </Scheduler>
            <AppointmentContextMenu
              menu={menu}
              menuRef={menuRef}
              parked={parked}
              onCopy={copyFromMenu}
              onCut={cutFromMenu}
              onPlace={placeFromMenu}
              onSetAttendance={handleMenuSetAttendance}
            />
          </div>
        </Col>
      </Row>
    </TitlelessCard>
  );
}

export default CalendarLocationPage;